/**
 * Bot local para las partidas contra la máquina.
 *
 * Es un buscador voraz de un solo nivel: en cada paso elige la jugada con mejor
 * puntuación heurística y repite hasta quedarse sin energía. No mira lo que hará
 * el rival ni los turnos futuros.
 *
 * Es suficiente y es lo correcto para esta fase: el bot solo tiene que ejercer
 * de sparring para probar el motor y la interfaz. La IA de verdad, si hace
 * falta, llegará cuando el PvP asíncrono defina qué es "jugar bien".
 *
 * No usa el RNG del estado a propósito: pensar no debe consumir azar de la
 * partida, porque eso desincronizaría una re-simulación en servidor.
 *
 * Está parametrizado por bando y no atado a OPPONENT: así se puede enfrentar
 * consigo mismo para medir sesgos del motor, y sirve para el autoplay de un
 * tutorial o de una repetición.
 */

import { getCardMaster } from '@/data';
import type { CardInstance, GameState, PlayerSide, Zone, ZoneIndex } from '@/types/game';
import { playCard } from './combatEngine';
import {
  basePowerOf,
  computeZonePower,
  isZoneLockedOnTurn,
  locationDeltaFor,
  occupancyOf,
  zoneCapacity,
} from './effects';
import { opponentOf, revealedCardsOf } from './state';

/** Una jugada concreta que el bot ha decidido hacer. */
export interface AiMove {
  instanceId: string;
  zone: ZoneIndex;
  /** Puntuación heurística, útil para depurar por qué eligió esto. */
  score: number;
}

/** Una recomendación para el jugador, con el motivo en castellano. */
export interface MoveSuggestion extends AiMove {
  /** Por qué se recomienda. Es lo que la convierte en consejo y no en orden. */
  reason: string;
}

/**
 * Cuánto poder espera aportar la carta si se juega en esta zona, contando su
 * efecto al revelarse. Es una estimación optimista: los efectos se evalúan
 * contra el tablero actual, no contra el que habrá tras la revelación.
 */
function estimatePower(
  state: GameState,
  card: CardInstance,
  zone: Zone,
  side: PlayerSide,
): number {
  const master = getCardMaster(card.masterId);
  const allies = revealedCardsOf(zone, side).length;
  const enemies = revealedCardsOf(zone, opponentOf(side)).length;
  let power = basePowerOf(card) + locationDeltaFor(zone.location, card);

  switch (master.effect.kind) {
    case 'BUFF_SELF_PER_ENEMY_HERE':
      power += enemies * master.effect.amountPer;
      break;
    case 'BUFF_SELF_IF_ALLIES_HERE':
      if (allies >= master.effect.minAllies) power += master.effect.amount;
      break;
    case 'DEBUFF_SELF_PER_ALLY_HERE':
      power -= allies * master.effect.amountPer;
      break;
    case 'DEBUFF_RANDOM_ENEMY_HERE':
      // Restarle poder al rival vale tanto como sumárselo a uno mismo.
      if (enemies > 0) power += master.effect.amount;
      break;
    case 'AURA_BUFF_ALLIES_HERE':
      // El aura rinde con los aliados que ya hay y con los que vengan después.
      power += master.effect.amount * (allies + 1);
      break;
    case 'BUFF_ALLIES_OTHER_ZONES': {
      const elsewhere = state.zones
        .filter((other) => other.index !== zone.index)
        .reduce((sum, other) => sum + revealedCardsOf(other, side).length, 0);
      power += master.effect.amount * elsewhere;
      break;
    }
    case 'DRAW_CARDS':
      // Una carta en mano vale aproximadamente dos de poder a estas alturas.
      power += master.effect.amount * 2;
      break;
    case 'GAIN_ENERGY_NEXT_TURN':
      power += master.effect.amount * 2;
      break;
    case 'NONE':
      break;
  }

  return power;
}

/**
 * Valor posicional de meter ese poder en esa zona.
 *
 * La idea: los puntos solo cuentan si ganan la zona. Reforzar una zona ya ganada
 * de calle no vale casi nada, y volcarse en una que se pierde por 20 tampoco.
 * Lo rentable está en el margen.
 */
/** Motivo dominante de una recomendación, para poder explicarla. */
type Rationale =
  | 'FLIP'
  | 'CLOSE_GAP'
  | 'HOLD_LEAD'
  | 'LOCATION'
  | 'SYNERGY'
  | 'VALUE';

function positionalBonus(
  state: GameState,
  zone: Zone,
  incomingPower: number,
  side: PlayerSide,
): number {
  const mine = computeZonePower(zone, side);
  const theirs = computeZonePower(zone, opponentOf(side));
  const gap = theirs - mine;

  // La zona se pierde y esta carta le da la vuelta: es la jugada más valiosa.
  if (gap > 0 && incomingPower > gap) return 6;
  // Se pierde y esta carta acerca de verdad: sigue siendo buena inversión.
  if (gap > 0 && incomingPower >= gap / 2) return 3;
  // Ya se gana con holgura: los puntos de más se desperdician.
  if (gap < -8) return -3;
  // Turnos finales: consolidar una ventaja corta evita perderla en el último.
  if (gap <= 0 && state.turn >= state.maxTurns - 1) return 2;
  return 0;
}

/**
 * Por qué esta jugada y no otra.
 *
 * Se deduce del factor que más pesó en la puntuación. No es una explicación
 * exhaustiva de la heurística —eso sería ilegible— sino la razón principal, que
 * es lo que un jugador necesita para aprender a decidir por su cuenta.
 */
function rationaleFor(
  state: GameState,
  card: CardInstance,
  zone: Zone,
  side: PlayerSide,
  power: number,
): Rationale {
  const mine = computeZonePower(zone, side);
  const theirs = computeZonePower(zone, opponentOf(side));
  const gap = theirs - mine;

  if (gap > 0 && power > gap) return 'FLIP';
  if (gap > 0 && power >= gap / 2) return 'CLOSE_GAP';
  if (gap <= 0 && state.turn >= state.maxTurns - 1) return 'HOLD_LEAD';

  if (locationDeltaFor(zone.location, card) > 0) return 'LOCATION';

  const master = getCardMaster(card.masterId);
  if (master.effectType !== 'NONE') return 'SYNERGY';
  return 'VALUE';
}

function explain(rationale: Rationale, zone: Zone, card: CardInstance): string {
  const master = getCardMaster(card.masterId);
  switch (rationale) {
    case 'FLIP':
      return `Le da la vuelta a ${zone.location.name}: ahí vas perdiendo y con esta carta pasarías a ganar.`;
    case 'CLOSE_GAP':
      return `Recorta distancia en ${zone.location.name}, que es la zona más disputada ahora mismo.`;
    case 'HOLD_LEAD':
      return `Asegura ${zone.location.name}: la ganas por poco y quedan pocos turnos para perderla.`;
    case 'LOCATION':
      return `${zone.location.name} le suma poder: ${zone.location.description.toLowerCase()}`;
    case 'SYNERGY':
      return `${master.name} rinde bien ahí. ${master.effectDescription}`;
    case 'VALUE':
      return `Es lo que más poder aporta con la energía que te queda.`;
  }
}

/**
 * Decide la mejor jugada disponible ahora mismo, o null si no queda ninguna
 * legal y asequible.
 */
function bestMove(state: GameState, side: PlayerSide): AiMove | null {
  const player = state.players[side];
  let best: AiMove | null = null;

  for (const card of player.hand) {
    const master = getCardMaster(card.masterId);
    if (master.cost > player.energy) continue;

    for (const zone of state.zones) {
      if (isZoneLockedOnTurn(zone, state.turn)) continue;
      if (occupancyOf(zone, side) >= zoneCapacity(zone)) continue;

      const power = estimatePower(state, card, zone, side);
      const score = power + positionalBonus(state, zone, power, side);
      if (!best || score > best.score) {
        best = { instanceId: card.instanceId, zone: zone.index, score };
      }
    }
  }

  return best;
}

/**
 * Juega el turno completo del bot y devuelve el estado resultante.
 *
 * Va jugando la mejor carta disponible mientras le quede energía. Como cada
 * jugada cambia el tablero, la siguiente se decide sobre el estado ya
 * actualizado: así el bot encadena combos simples (colocar el Bardo y después
 * apilar aliados en su zona) sin planificarlos explícitamente.
 */
/**
 * Recomienda una jugada al jugador, con el motivo.
 *
 * Usa exactamente la misma heurística que mueve al bot. No es una casualidad
 * cómoda: es la garantía de que el consejo es tan bueno como el rival, ni más ni
 * menos. Un ayudante mejor que la IA haría el juego trivial; uno peor engañaría.
 *
 * Es un consejo y no un piloto automático: sugiere una jugada cada vez y nunca
 * la ejecuta.
 */
export function suggestMove(state: GameState, side: PlayerSide): MoveSuggestion | null {
  if (state.phase !== 'PLANNING') return null;

  const move = bestMove(state, side);
  if (!move) return null;

  const card = state.players[side].hand.find((item) => item.instanceId === move.instanceId);
  if (!card) return null;

  const zone = state.zones[move.zone];
  const power = estimatePower(state, card, zone, side);
  const rationale = rationaleFor(state, card, zone, side, power);

  return { ...move, reason: explain(rationale, zone, card) };
}

export function runOpponentTurn(
  state: GameState,
  side: PlayerSide = 'OPPONENT',
): { state: GameState; moves: AiMove[] } {
  let current = state;
  const moves: AiMove[] = [];

  // Cota dura por si una regla futura hiciera que una jugada no consumiera
  // energía: preferimos un bot subóptimo a un bucle infinito.
  const maxPlays = 12;

  for (let i = 0; i < maxPlays; i += 1) {
    const move = bestMove(current, side);
    if (!move) break;

    const result = playCard(current, side, move.instanceId, move.zone);
    if (!result.ok) break;

    current = result.state;
    moves.push(move);
  }

  return { state: current, moves };
}
